import { useState } from 'react';
import { Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Input } from '~/components/ui/inputs/Input';
import { StepIndicator } from '~/components/ui';
import {
  useCreateOrganization,
  useCreateTenantAdmin,
} from '~/api/organizations';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
}

type WizardStep = 1 | 2;

export default function CreateOrganizationModal({
  open,
  onClose,
}: CreateOrganizationModalProps) {
  const { t } = useTranslation('organizations');
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [organizationName, setOrganizationName] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentStep(1);
      setOrganizationName('');
      setAdminFirstName('');
      setAdminLastName('');
      setAdminEmail('');
      setIsSubmitting(false);
    }
  }

  const createOrganization = useCreateOrganization();
  const createTenantAdmin = useCreateTenantAdmin();

  const isStep1Valid = organizationName.trim().length > 0;
  const isStep2Valid =
    adminFirstName.trim().length > 0 &&
    adminLastName.trim().length > 0 &&
    adminEmail.trim().length > 0;

  function handleClose() {
    onClose();
  }

  function handleOrganizationNameChange(fieldName: string, value: string) {
    if (fieldName === 'organizationName') setOrganizationName(value);
  }

  function handleAdminFieldChange(fieldName: string, value: string) {
    if (fieldName === 'adminFirstName') setAdminFirstName(value);
    if (fieldName === 'adminLastName') setAdminLastName(value);
    if (fieldName === 'adminEmail') setAdminEmail(value);
  }

  function handleAdvanceToStep2() {
    if (!isStep1Valid) return;
    setCurrentStep(2);
  }

  function handleBackToStep1() {
    setCurrentStep(1);
  }

  async function handleSkip() {
    setIsSubmitting(true);
    try {
      await createOrganization.mutateAsync({
        input: { name: organizationName.trim() },
      });
      toast.success(t('organizations.create.success'));
      handleClose();
    } catch (error) {
      toast.error(
        extractGraphQLErrorMessages(error as ClientError).join(', ') ||
          t('organizations.create.error')
      );
      setIsSubmitting(false);
    }
  }

  async function handleCreateWithAdmin() {
    if (!isStep2Valid) return;
    setIsSubmitting(true);
    try {
      const result = await createOrganization.mutateAsync({
        input: { name: organizationName.trim() },
      });
      const organizationId = result.createOrganization.id;
      try {
        await createTenantAdmin.mutateAsync({
          input: {
            organizationId,
            firstName: adminFirstName.trim(),
            lastName: adminLastName.trim(),
            email: adminEmail.trim(),
          },
        });
        toast.success(t('organizations.create.successWithAdmin'));
      } catch {
        toast.error(t('organizations.create.adminError'));
      }
      handleClose();
    } catch (error) {
      toast.error(
        extractGraphQLErrorMessages(error as ClientError).join(', ') ||
          t('organizations.create.error')
      );
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-[28rem] max-w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-prim-100 rounded-lg">
            <Building2 size={20} className="text-prim" />
          </div>
          <h2 id="Modal-title" className="text-lg font-semibold text-neut-prim">
            {t('organizations.create.title')}
          </h2>
        </div>

        <div className="mb-6">
          <StepIndicator
            steps={[
              { id: '1', label: t('organizations.create.step_details') },
              { id: '2', label: t('organizations.create.step_admin') },
            ]}
            currentStepId={String(currentStep)}
            testId="create-organization-step-indicator"
          />
        </div>

        {/* Step 1 — Organization details */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Input
              label={t('organizations.create.nameLabel')}
              name="organizationName"
              value={organizationName}
              onChange={handleOrganizationNameChange}
              placeholder={t('organizations.create.namePlaceholder')}
              required
            />

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors"
              >
                {t('organizations.create.cancel')}
              </button>
              <button
                type="button"
                disabled={!isStep1Valid || isSubmitting}
                onClick={handleAdvanceToStep2}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-prim rounded-lg hover:bg-prim-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('organizations.create.next')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Tenant Admin (optional) */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-neut-sec">
              {t('organizations.create.adminSubtitle')}
            </p>

            <Input
              label={t('organizations.admin.firstName')}
              name="adminFirstName"
              value={adminFirstName}
              onChange={handleAdminFieldChange}
              placeholder={t('organizations.admin.firstNamePlaceholder')}
            />
            <Input
              label={t('organizations.admin.lastName')}
              name="adminLastName"
              value={adminLastName}
              onChange={handleAdminFieldChange}
              placeholder={t('organizations.admin.lastNamePlaceholder')}
            />
            <Input
              label={t('organizations.admin.email')}
              name="adminEmail"
              value={adminEmail}
              onChange={handleAdminFieldChange}
              placeholder={t('organizations.admin.emailPlaceholder')}
            />

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleBackToStep1}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors disabled:opacity-50"
              >
                {t('organizations.create.back')}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleSkip}
                className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors disabled:opacity-50"
              >
                {isSubmitting
                  ? t('organizations.create.submitting')
                  : t('organizations.create.skip')}
              </button>
              <button
                type="button"
                disabled={!isStep2Valid || isSubmitting}
                onClick={handleCreateWithAdmin}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-prim rounded-lg hover:bg-prim-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting
                  ? t('organizations.create.submitting')
                  : t('organizations.create.submit')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
